"use client";

import { forwardRef } from "react";

const Button = forwardRef(({ 
  children, 
  variant = "primary", 
  size = "md", 
  disabled = false, 
  loading = false,
  onClick, 
  className = "",
  style = {},
  ...props 
}, ref) => {
  const baseStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    border: "none",
    borderRadius: "8px",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontWeight: 500,
    cursor: disabled || loading ? "not-allowed" : "pointer",
    transition: "all 150ms cubic-bezier(0.0, 0.0, 0.2, 1)",
    outline: "none",
    position: "relative",
    overflow: "hidden",
    ...style
  };

  const variants = {
    primary: {
      backgroundColor: disabled ? "#94a3b8" : "#2563eb",
      color: "#ffffff",
      boxShadow: disabled ? "none" : "0 4px 14px rgba(37, 99, 235, 0.35)",
      "&:hover": !disabled && {
        backgroundColor: "#1d4ed8",
        boxShadow: "0 6px 20px rgba(37, 99, 235, 0.45)",
        transform: "translateY(-1px)"
      },
      "&:active": !disabled && {
        transform: "translateY(0px) scale(0.99)"
      }
    },
    secondary: {
      backgroundColor: "transparent",
      color: disabled ? "#94a3b8" : "#0f172a",
      border: "1.5px solid #cbd5e1",
      "&:hover": !disabled && {
        backgroundColor: "#f8fafc",
        borderColor: "#2563eb",
        color: "#2563eb"
      }
    },
    destructive: {
      backgroundColor: disabled ? "#94a3b8" : "#ef4444",
      color: "#ffffff",
      "&:hover": !disabled && {
        backgroundColor: "#dc2626",
        boxShadow: "0 4px 14px rgba(239, 68, 68, 0.35)"
      }
    },
    ghost: {
      backgroundColor: "transparent",
      color: disabled ? "#94a3b8" : "#475569",
      "&:hover": !disabled && {
        backgroundColor: "#f1f5f9",
        color: "#0f172a"
      }
    }
  };

  const sizes = {
    sm: {
      fontSize: "0.875rem",
      padding: "0.5rem 1rem",
      minHeight: "32px"
    },
    md: {
      fontSize: "0.9375rem", 
      padding: "0.625rem 1.25rem",
      minHeight: "40px"
    },
    lg: {
      fontSize: "1rem",
      padding: "0.75rem 1.5rem",
      minHeight: "44px"
    }
  };

  const Spinner = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ animation: "spin 0.7s linear infinite" }}
    >
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="18.85 18.85"
        opacity="0.3"
      />
      <circle
        cx="8"
        cy="8"
        r="6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="18.85 18.85"
        strokeDashoffset="4.71"
      />
    </svg>
  );

  return (
    <>
      <style jsx>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <button
        ref={ref}
        className={`btn btn-${variant} btn-${size} ${className}`}
        disabled={disabled || loading}
        onClick={onClick}
        style={{
          ...baseStyle,
          ...variants[variant],
          ...sizes[size],
          ...(disabled || loading ? { opacity: 0.6 } : {})
        }}
        {...props}
      >
        {loading && <Spinner />}
        {children}
      </button>
    </>
  );
});

Button.displayName = "Button";

export { Button };
